import * as React from 'react'

import { Box, Button, Icon, ProgressBar, Text } from '@factorialco/deprecated-design-system'
import Decimal from 'decimal.js'
import { PerformanceReviewerStrategiesStrategyEnum } from 'generated/resources/datatable.graphql'

import { useFormContext } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import type { Form } from 'modules/performance_process/components/score_configuration_weights/ScoreWeightsSidepanel'
import type { WeightValidationStatus } from 'modules/performance_process/components/score_configuration_weights/ScoreWeightsSidepanel'
import { type MappedStrategyQuestionnaire } from 'modules/performance_process/hooks/useMappedQuestionnaires'
import { useSectionWeights } from 'modules/performance_process/hooks/useStrategiesScoreConfiguration'

const getWeightValidationStatus = (weight: number): WeightValidationStatus => {
  if (weight === 100) return 'ok'

  return weight < 100 ? 'not_enough' : 'exceed'
}

type Props = {
  strategy: PerformanceReviewerStrategiesStrategyEnum
  questionnaire: MappedStrategyQuestionnaire
}

export const SectionWeightsProgress: React.FC<Props> = ({ strategy, questionnaire }) => {
  const form = useFormContext<Form>()
  const configuration = form.values.configurationByStrategy[strategy]
  const weights = configuration?.sectionWeights || []
  const errorMessages: Record<WeightValidationStatus, string | null> = {
    not_enough: i18n.t('performance_process.section_weights.weight_errors.not_enough', {
      weight: 100,
    }),
    exceed: i18n.t('performance_process.section_weights.weight_errors.exceed', {
      weight: 100,
    }),
    ok: null,
  }
  const { sectionWeights, isSectionWeightable } = useSectionWeights({
    questionnaire: questionnaire,
    weights: weights,
  })
  const weightableSectionIds = new Set(
    Object.keys(sectionWeights).filter(
      (sectionId) => sectionWeights[sectionId] === 0 && isSectionWeightable(sectionId)
    )
  )

  const currentTotal = Decimal.sum(0, ...weights.map(({ weight }) => weight)).toNumber()
  const validationStatus = getWeightValidationStatus(currentTotal)
  const canDistribute = validationStatus === 'not_enough' && weightableSectionIds.size > 0

  const handleClick = () => {
    const necessaryWeight = new Decimal(100)
    const totalWeightDecimal = new Decimal(currentTotal)
    const remaining = necessaryWeight.minus(totalWeightDecimal)

    if (remaining.gt(0)) {
      const amountToDistribute = remaining
        .div(weightableSectionIds.size)
        .toDecimalPlaces(1)
        .toNumber()

      const newSectionWeights = Object.entries(sectionWeights)
        .filter(([id]) => isSectionWeightable(id))
        .map(([sectionId, weight]) => ({
          sectionId,
          weight: weightableSectionIds.has(sectionId) ? amountToDistribute : weight,
        }))
      form.setFieldValue(`configurationByStrategy.${strategy}`, {
        ...configuration,
        ...{ sectionWeights: newSectionWeights },
      })
    }
  }

  if (!configuration || !configuration.producesScore) {
    return null
  }

  return (
    <Box gap="s16" flexDirection="column">
      <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
        <Text.Normal size="400" weight="semibold" color="textPrimary">
          {i18n.t('performance_process.section_weights.sidepanel_description')}
        </Text.Normal>
        <Button
          type="secondary"
          onClick={handleClick}
          disabled={form.isSubmitting || !canDistribute}
        >
          {i18n.t('performance_process.section_weights.distribute_remaining')}
        </Button>
      </Box>
      <>
        <Box flexDirection="row" gap="s8" alignItems="center">
          <ProgressBar
            total={100}
            amount={currentTotal}
            progressColor={validationStatus === 'exceed' ? 'radicalRed1000' : 'viridian1000'}
            barColor="grey400"
            showLabel
          />
          <Icon.Large
            icon={validationStatus === 'ok' ? 'check' : 'alert'}
            color={validationStatus === 'ok' ? 'viridian1000' : 'radicalRed1000'}
          />
        </Box>
        <Box minHeight="s20">
          <Text.Normal size="200" weight="normal" color="radicalRed1000">
            {validationStatus !== 'ok' && errorMessages[validationStatus]}
          </Text.Normal>
        </Box>
      </>
    </Box>
  )
}
